import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ReservationForm from 'reservation-form-easoft';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const PARIS_TZ = 'Europe/Paris';
const CONFIRMED_STATUSES = [2, 3];

const getResto = async (restoName) => {
  const docs = await firebase.firestore()
    .collection('Restaurant')
    .where('urlName', '==', restoName)
    .get();

  if (!docs.empty) {
    return {
      id: docs.docs[0].id,
      ...docs.docs[0].data(),
    };
  }
  return null;
};

const addReservation = (resto, reservation) => {
  reservation = !resto.reservationSettings.autoConfirm
    ? { ...reservation, status: 1 }
    : { ...reservation, status: 3 };
  return firebase.firestore()
    .collection('Restaurant')
    .doc(resto.id)
    .collection('Reservation')
    .add(reservation);
};

const parseScheduleEntry = (entry) => {
  const range = typeof entry === 'string' ? entry : (entry && entry.range) || '';
  const limit =
    entry && typeof entry === 'object' && Number.isFinite(entry.limit) ? entry.limit : null;
  const [start, end] = range.split('-');
  return start && end ? { start, end, limit } : null;
};

const computeUnavailableSlots = (schedule, confirmedReservations, maxDaysInAdvance) => {
  const unavailable = new Set();
  if (!schedule) return unavailable;

  const today = dayjs().tz(PARIS_TZ).startOf('day');

  for (let d = 0; d <= maxDaysInAdvance; d++) {
    const day = today.add(d, 'day');
    const isoDay = (day.day() + 6) % 7;
    const rawEntries = schedule[isoDay] || [];
    const entries = rawEntries.map(parseScheduleEntry).filter(Boolean);

    for (const { start, end, limit } of entries) {
      if (!Number.isFinite(limit) || limit <= 0) continue;

      const dayKey = day.format('YYYY-MM-DD');
      const windowStart = dayjs.tz(`${dayKey} ${start}`, 'YYYY-MM-DD H:mm', PARIS_TZ);
      const windowEnd = dayjs.tz(`${dayKey} ${end}`, 'YYYY-MM-DD H:mm', PARIS_TZ);
      if (!windowStart.isValid() || !windowEnd.isValid()) continue;

      const isSingleSlot = windowStart.isSame(windowEnd);

      const total = confirmedReservations.reduce((acc, r) => {
        const rTime = dayjs(r.date).tz(PARIS_TZ);
        const inWindow = isSingleSlot
          ? rTime.isSame(windowStart)
          : (rTime.isSame(windowStart) || rTime.isAfter(windowStart)) && rTime.isBefore(windowEnd);
        return inWindow ? acc + (Number(r.nbAdult) || 0) : acc;
      }, 0);

      if (total >= limit) {
        let slot = windowStart;
        while (slot.isBefore(windowEnd) || slot.isSame(windowEnd)) {
          unavailable.add(slot.format('YYYY-MM-DD HH:mm'));
          slot = slot.add(15, 'minute');
        }
      }
    }
  }

  return unavailable;
};

const Reservation = () => {
  const { restoName } = useParams();
  const [resto, setResto] = useState(null);
  const [error, setError] = useState(null);
  const [confirmedReservations, setConfirmedReservations] = useState([]);

  useEffect(() => {
    getResto(restoName.toLowerCase()).then(r => {
      if (r === null) setError("Le restaurant n'existe pas");
      else setResto(r);
    });
  }, [restoName]);

  useEffect(() => {
    if (resto !== null) {
      document.title = 'Reservation ' + resto.reservationSettings.restoName;
    }
  }, [resto]);

  const maxDaysInAdvance = resto?.reservationSettings?.maxDaysInAdvance ?? 30;

  useEffect(() => {
    if (!resto) return;

    const startDate = dayjs().tz(PARIS_TZ).startOf('day').toDate();
    const endDate = dayjs().tz(PARIS_TZ).add(maxDaysInAdvance, 'day').endOf('day').toDate();

    const unsubscribe = firebase.firestore()
      .collection('Restaurant')
      .doc(resto.id)
      .collection('Reservation')
      .where('status', 'in', CONFIRMED_STATUSES)
      .where('reservationDate', '>=', startDate)
      .where('reservationDate', '<=', endDate)
      .onSnapshot(snap => {
        const confirmed = snap.docs.map(doc => {
          const data = doc.data();
          return {
            date: data.reservationDate.toDate(),
            nbAdult: data.nbAdult || 0,
          };
        });
        setConfirmedReservations(confirmed);
      }, (err) => {
        console.error('[Reservation] reservations listener failed:', err);
      });

    return unsubscribe;
  }, [resto, maxDaysInAdvance]);

  const unavailableSlots = useMemo(() => {
    if (!resto) return [];
    const set = computeUnavailableSlots(
      resto.reservationSettings?.schedule,
      confirmedReservations,
      maxDaysInAdvance,
    );
    return Array.from(set);
  }, [resto, confirmedReservations, maxDaysInAdvance]);

  const onConfirm = useCallback((reservation) => {
    return addReservation(resto, reservation);
  }, [resto]);

  if (resto !== null) {
    const closedDates = (resto.reservationSettings.closedDates ?? []).map(t => t.toDate());

    return (
      <ReservationForm
        reservationSettings={{
          imgPath: resto.photos?.profilPath ?? 'https://picsum.photos/600/400',
          ...resto.reservationSettings,
          closedDates,
        }}
        unavailableSlots={unavailableSlots}
        onConfirm={onConfirm}
        restoId={resto.id}
      />
    );
  }

  if (error !== null) return <h1>{error}</h1>;
  return <h1>Chargement...</h1>;
};

export default Reservation;
